import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import Grid from '@components-clean/core/grid-fe';
import { OpraReportTemplate } from '@interfaces/report-templates.interface';
import { TEMPLATE_GRID_COLUMNS } from '@pages-clean/shared/report-template-grid/grid-columns';
import { mapApplicationArea, mapService } from '@utils-clean/grid-download-audit-helper';
import { AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import Constants, { ModuleNames } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import {
    addPrefixedOrganisationTemplateId,
    addPrefixedRecordIdsAndTemplateNamesToReportTemplates,
} from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { useGetReportTemplatesForOrgByArea } from '@apis/bff-api/report-templates';

type Props = {
    module: number;
    studyId: string;
    orgKey: string;
    onTemplateSelected: (template: OpraReportTemplate) => void;
};

export const ReportTemplateGrid = ({ module, studyId, orgKey, onTemplateSelected }: Props) => {
    const { t } = useTranslation();
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const columns = useMemo(() => TEMPLATE_GRID_COLUMNS(t, module), [module]);
    const localState = useLocalState();

    const { data, isLoading, isValidating } = useGetReportTemplatesForOrgByArea(orgKey, module, studyId);
    const { data: prefixesData } = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        fetchPolicy: 'cache-first',
    });
    const reportTemplates = useMemo(() => {
        if (!data || !prefixesData) return [];
        const templates = addPrefixedRecordIdsAndTemplateNamesToReportTemplates(
            data,
            prefixesData?.auditRecordIdPrefixes,
        );
        const fullyFormedTemplates = addPrefixedOrganisationTemplateId(
            templates,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        return orderBy(fullyFormedTemplates, [{ field: 'fullName', dir: 'asc' }]);
    }, [data, prefixesData]);

    const studyName = localState.state?.selectedStudy?.studyNumber;
    const moduleName =
        module === Constants.Modules.RAM
            ? ModuleNames.RAM
            : module === Constants.Modules.SM
              ? ModuleNames.SM
              : ModuleNames.CM;
    const exportFileName = `Study ${moduleName} Report Templates_${studyName}_${stringDateWithoutTimeFromUtc(
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/purity
        new Date(),
        'DD-MMM-YYYY',
    )}`;

    return (
        <Grid
            maxHeight="auto"
            testDataId="generated-reports-grid"
            data={reportTemplates}
            dataItemKey="id"
            showPlaceholder={false}
            sortable
            filterable
            columns={columns}
            title={t('templates')}
            showAddIcon={false}
            loading={isLoading || isValidating}
            selectable={{ mode: 'single', enabled: true }}
            onSelectionChange={(e) => onTemplateSelected(e.startDataItem as OpraReportTemplate)}
            exportFilename={exportFileName}
            service={mapService(module)}
            area={mapApplicationArea(module)}
            dataType={AuditDataType.EXCEL_EXPORT}
        />
    );
};
